/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, IconButton } from '@mui/material';
import React from 'react';
import type { ItemInterface } from 'react-sortablejs';
import { ReactSortable } from 'react-sortablejs';
import { DragHandle } from '@opengeoweb/theme';
import { SORTABLE_PANEL_GAP, usePanelSizes } from './usePanelSizes';
import { ResizablePanelStandalone } from '../ResizablePanel/ResizablePanelStandalone';
import type { ResizablePanelComponent } from '../ResizablePanel/ResizablePanelStandalone';

export interface SortablePanelItem {
  id: string;
  name: string;
  isDisplayed: boolean;
  isOpen?: boolean;
  customSize?: number;
  minWidth?: number;
  maxWidth?: number;
  headerAction?: React.ReactElement;
  render: () => React.ReactNode;
  panelRef?: React.Ref<HTMLDivElement>;
  onOpenChange?: (isOpen: boolean) => void;
  onResizeStart?: () => void;
  onResizeEnd?: () => void;
}

export interface SortablePanelsProps {
  panels: SortablePanelItem[];
  onOrderChange: (
    newOrder: { id: string; name: string; isDisplayed: boolean }[],
  ) => void;
  panelComponent?: ResizablePanelComponent;
  /**
   * Optional. Forwarded to each rendered panel component. Redux-aware panel
   * components use it to scope their external state by parent view.
   */
  viewId?: string;
  /** Direction in which panels are laid out, sorted and resized. Defaults to 'horizontal'. */
  direction?: 'horizontal' | 'vertical';
  /** Fit unsized panels once, or follow the viewport until a manual resize. */
  sizing?: 'initial' | 'untilManualResize';
  /** Preserve panel sizes and scroll when they exceed the available space. */
  allowOverflow?: boolean;
  /** Explicitly grow open panels in proportion to their current sizes. */
  resizeOnViewCloseSignal?: number;
}

interface SortableItem extends ItemInterface {
  id: string;
  name: string;
  isDisplayed: boolean;
}

export const SortablePanels = ({
  panels,
  onOrderChange,
  panelComponent: PanelComponent = ResizablePanelStandalone,
  viewId,
  direction = 'horizontal',
  sizing,
  allowOverflow = false,
  resizeOnViewCloseSignal,
}: SortablePanelsProps): React.ReactElement => {
  const {
    viewportRef,
    rootRef,
    sizes,
    onSizeChange,
    onOpenChange,
    onResizeStart,
  } = usePanelSizes(panels, direction, sizing, resizeOnViewCloseSignal);
  const content = (
    <Box
      ref={rootRef}
      sx={{
        display: 'flex',
        flexDirection: direction === 'vertical' ? 'column' : 'row',
        width: '100%',
        // Vertical lists must grow with their panels so the scroll container
        // includes the final panel and the bottom padding.
        height: direction === 'vertical' && allowOverflow ? 'auto' : '100%',
        boxSizing: 'border-box',
        ...(direction === 'vertical' &&
          allowOverflow && {
            minHeight: '100%',
          }),
        padding: 1,
        ...(sizing &&
          direction === 'horizontal' &&
          allowOverflow && {
            width: 'max-content',
            minWidth: '100%',
          }),
      }}
    >
      <ReactSortable<SortableItem>
        animation={200}
        direction={direction}
        draggable=".sortable-item"
        handle=".drag-handle"
        list={panels.map(({ id, name, isDisplayed }) => ({
          id,
          name,
          isDisplayed,
        }))}
        setList={(list) =>
          onOrderChange(
            list.map(({ id, name, isDisplayed }) => ({
              id,
              name,
              isDisplayed,
            })),
          )
        }
        style={{
          display: 'flex',
          flexDirection: direction === 'vertical' ? 'column' : 'row',
          gap: SORTABLE_PANEL_GAP,
          width: '100%',
          minWidth: 0,
          minHeight: 0,
          ...(direction === 'vertical' && allowOverflow && { flexShrink: 0 }),
        }}
        tag="div"
      >
        {panels.map((panel) => (
          <PanelComponent
            allowOverflow={allowOverflow}
            direction={direction}
            handleButtonComponent={
              <IconButton
                aria-label="drag-handle"
                className="drag-handle"
                size="small"
                sx={{ width: '24px', height: '24px' }}
              >
                <DragHandle fontSize="small" />
              </IconButton>
            }
            headerAction={panel.headerAction}
            initialSize={sizing ? sizes[panel.id] : undefined}
            isDisplayed={panel.isDisplayed}
            key={panel.id}
            maxWidth={panel.maxWidth}
            minWidth={panel.minWidth}
            name={panel.name}
            onOpenChange={(open) => {
              if (sizing) {
                onOpenChange(panel.id, open);
              }
              panel.onOpenChange?.(open);
            }}
            onResizeEnd={panel.onResizeEnd}
            onResizeStart={() => {
              if (sizing) {
                onResizeStart();
              }
              panel.onResizeStart?.();
            }}
            onSizeChange={
              sizing ? (size): void => onSizeChange(panel.id, size) : undefined
            }
            panelId={panel.id}
            ref={panel.panelRef}
            viewId={viewId}
          >
            {panel.render()}
          </PanelComponent>
        ))}
      </ReactSortable>
    </Box>
  );
  return sizing ? (
    <Box
      ref={viewportRef}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        minWidth: 0,
        overflow: allowOverflow ? 'auto' : 'hidden',
      }}
    >
      {content}
    </Box>
  ) : (
    content
  );
};
